import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import './TipTapEditor.css';

export const TipTapEditor = ({ content, onChange, placeholder = 'Start writing...' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3, 4, 5]
        },
        paragraph: {
          HTMLAttributes: {
            class: 'editor-paragraph'
          }
        }
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer'
        }
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph', 'blockquote']
      }),
      Table.configure({
        resizable: false,
        HTMLAttributes: {
          class: 'editor-table'
        }
      }),
      TableRow.configure({
        HTMLAttributes: {
          class: 'editor-table-row'
        }
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'editor-table-header'
        }
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'editor-table-cell'
        }
      })
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      if (onChange) {
        const jsonContent = editor.getJSON();
        onChange(jsonContent);
      }
    },
    editorProps: {
      attributes: {
        class: 'prose-editor focus:outline-none min-h-[400px] p-6'
      }
    }
  });

  React.useEffect(() => {
    if (editor && content) {
      const currentContent = editor.getJSON();
      const contentChanged = JSON.stringify(currentContent) !== JSON.stringify(content);
      
      if (contentChanged) {
        editor.commands.setContent(content);
      }
    }
  }, [content, editor]);

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl || '');
    
    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  if (!editor) {
    return null;
  }

  return (
    <div className="tiptap-editor-enhanced border-2 border-[#E5E7EB] rounded-lg bg-white">
      <div className="toolbar bg-[#F8FAFC] border-b-2 border-[#E5E7EB] p-3 flex flex-wrap gap-2 sticky top-0 z-10">
        {/* History */}
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className={`toolbar-btn-enhanced ${!editor.can().undo() ? 'opacity-50 cursor-not-allowed' : ''}`}
          title="Undo"
        >
          ↶ Undo
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className={`toolbar-btn-enhanced ${!editor.can().redo() ? 'opacity-50 cursor-not-allowed' : ''}`}
          title="Redo"
        >
          ↷ Redo
        </button>
        
        <div className="toolbar-divider" />

        {/* Headings */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`toolbar-btn-enhanced ${editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}`}
          title="Heading 2"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`toolbar-btn-enhanced ${editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}`}
          title="Heading 3"
        >
          H3
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
          className={`toolbar-btn-enhanced ${editor.isActive('heading', { level: 4 }) ? 'is-active' : ''}`}
          title="Heading 4"
        >
          H4
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
          className={`toolbar-btn-enhanced ${editor.isActive('heading', { level: 5 }) ? 'is-active' : ''}`}
          title="Heading 5"
        >
          H5
        </button>
        
        <div className="toolbar-divider" />
        
        {/* Text Formatting */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`toolbar-btn-enhanced ${editor.isActive('bold') ? 'is-active' : ''}`}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`toolbar-btn-enhanced ${editor.isActive('italic') ? 'is-active' : ''}`}
          title="Italic"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`toolbar-btn-enhanced ${editor.isActive('underline') ? 'is-active' : ''}`}
          title="Underline"
        >
          <u>U</u>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          className="toolbar-btn-enhanced"
          title="Clear Formatting"
        >
          Clear
        </button>

        <div className="toolbar-divider" />
        
        {/* Lists */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`toolbar-btn-enhanced ${editor.isActive('bulletList') ? 'is-active' : ''}`}
          title="Bullet List"
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`toolbar-btn-enhanced ${editor.isActive('orderedList') ? 'is-active' : ''}`}
          title="Numbered List"
        >
          1. List
        </button>
        
        <div className="toolbar-divider" />
        
        {/* Quote & Rule */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`toolbar-btn-enhanced ${editor.isActive('blockquote') ? 'is-active' : ''}`}
          title="Blockquote"
        >
          " Quote
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className="toolbar-btn-enhanced"
          title="Horizontal Rule"
        >
          — Rule
        </button>
        
        <div className="toolbar-divider" />
        
        {/* Alignment */}
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`toolbar-btn-enhanced ${editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}`}
          title="Align Left"
        >
          ⬅ Left
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`toolbar-btn-enhanced ${editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}`}
          title="Align Center"
        >
          ⬌ Center
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`toolbar-btn-enhanced ${editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}`}
          title="Align Right"
        >
          ➡ Right
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          className={`toolbar-btn-enhanced ${editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}`}
          title="Justify"
        >
          ≡ Justify
        </button>

        <div className="toolbar-divider" />

        {/* Tables */}
        <button
          type="button"
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          className="toolbar-btn-enhanced"
          title="Insert Table"
        >
          Table
        </button>
        {editor.isActive('table') && (
          <>
            <button
              type="button"
              onClick={() => editor.chain().focus().addColumnBefore().run()}
              className="toolbar-btn-enhanced"
              title="Add Column Before"
            >
              + Col
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              className="toolbar-btn-enhanced"
              title="Add Column After"
            >
              Col +
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().deleteColumn().run()}
              className="toolbar-btn-enhanced"
              title="Delete Column"
            >
              - Col
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().addRowBefore().run()}
              className="toolbar-btn-enhanced"
              title="Add Row Before"
            >
              + Row
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().addRowAfter().run()}
              className="toolbar-btn-enhanced"
              title="Add Row After"
            >
              Row +
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().deleteRow().run()}
              className="toolbar-btn-enhanced"
              title="Delete Row"
            >
              - Row
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().deleteTable().run()}
              className="toolbar-btn-enhanced"
              title="Delete Table"
            >
              × Table
            </button>
          </>
        )}

        <div className="toolbar-divider" />
        
        {/* Links */}
        <button
          type="button"
          onClick={setLink}
          className={`toolbar-btn-enhanced ${editor.isActive('link') ? 'is-active' : ''}`}
          title="Insert/Edit Link"
        >
          🔗 Link
        </button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default TipTapEditor;
